import { findOperationByIdOrErrorFromDefinition } from "@/lib/editor-mutations/oas-operations";
import { NoRefsHereError } from "@/lib/errors";
import { deref, isReference } from "@/lib/oas-tools/oas-tag-helpers";
import {
  OASDefinition,
  OASReferenceObject,
  OASRequestBodyObject,
  SupportedContentFormats,
} from "@/lib/types";

export function getRequestBodyFromDefinition({
  operationId,
  definition,
}: {
  operationId: string;
  definition: OASDefinition;
}) {
  const operation = findOperationByIdOrErrorFromDefinition(
    definition,
    operationId
  );
  return operation.requestBody;
}

export function getRequestBodyOrErrorFromDefinition({
  definition,
  operationId,
}: {
  definition: OASDefinition;
  operationId: string;
}) {
  const found = getRequestBodyFromDefinition({ definition, operationId });
  if (!found) throw new Error("RequestBody not found");
  return found;
}

export function resolveRequestBodyFromDefinition({
  definition,
  requestBody,
}: {
  definition: OASDefinition;
  requestBody: OASRequestBodyObject | OASReferenceObject;
}) {
  if (!isReference(requestBody)) return requestBody;

  const found = definition.components?.requestBodies?.[deref(requestBody.$ref)];
  if (!found) throw new Error("Reference not found");
  if (isReference(found))
    throw new Error("Component parameters cannot be references");

  return found;
}

export function getRequestBodySchemaOrErrorFromDefinition({
  operationId,
  definition,
  format,
}: {
  operationId: string;
  definition: OASDefinition;
  format: SupportedContentFormats;
}) {
  const requestBody = getRequestBodyOrErrorFromDefinition({
    definition,
    operationId,
  });

  if (isReference(requestBody)) throw new NoRefsHereError();

  const schema = requestBody.content?.[format].schema;
  if (!schema) throw new Error("RequestBody schema not found");

  return schema;
}

export function addRequestBodyToDefinition({
  operationId,
  requestBody,
  definition,
}: {
  operationId: string;
  requestBody: OASRequestBodyObject;
  definition: OASDefinition;
}) {
  const cp = structuredClone(definition);
  const operation = findOperationByIdOrErrorFromDefinition(cp, operationId);
  operation.requestBody = requestBody;
  return cp;
}

export function removeRequestBodyFromDefinition({
  operationId,
  definition,
}: {
  operationId: string;
  definition: OASDefinition;
}) {
  const cp = structuredClone(definition);
  const operation = findOperationByIdOrErrorFromDefinition(cp, operationId);
  delete operation.requestBody;
  return cp;
}
